import { useEffect, useState } from "react";
import { Bot, Copy, Check, Zap, Loader2, ExternalLink, Activity, Code2, Sparkles } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { toast } from "sonner";

export default function OttoPage() {
  const { active } = useProjects();
  const [install, setInstall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    api.get(`/otto/status?project_id=${active.id}`)
      .then(({ data }) => setInstall(data.installed ? data : null))
      .catch(() => setInstall(null))
      .finally(() => setLoading(false));
  }, [active?.id]);

  const generate = async () => {
    if (!active) return;
    setInstalling(true);
    try {
      const { data } = await api.post("/otto/install", { project_id: active.id });
      setInstall(data);
      toast.success("OTTO snippet generated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInstalling(false);
    }
  };

  const copySnippet = () => {
    if (!install?.snippet) return;
    navigator.clipboard.writeText(install.snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!active) return null;

  return (
    <DashboardLayout
      eyebrow="One-paste auto-SEO"
      title="OTTO · Live SEO Engine"
      subtitle="Paste one script tag into your site. OTTO injects every audit fix automatically — no CMS, no redeploys, forever."
    >
      <div className="space-y-6" data-testid="otto-page">
        {/* Hero stats */}
        <div className="grid sm:grid-cols-3 gap-3">
          <StatCard
            label="Status"
            value={install?.alive ? "LIVE" : install ? "Awaiting first ping" : "Not installed"}
            icon={Activity}
            color={install?.alive ? "#10B981" : "#71717A"}
            testid="otto-status"
          />
          <StatCard
            label="Fixes pushed live"
            value={(install?.fixes_pushed ?? 0).toLocaleString()}
            icon={Zap}
            color="#0055FF"
            testid="otto-fixes-count"
          />
          <StatCard
            label="Last seen"
            value={install?.last_seen_at ? new Date(install.last_seen_at).toLocaleString() : "—"}
            icon={Sparkles}
            color="#3377FF"
            small
            testid="otto-last-seen"
          />
        </div>

        {/* Snippet card */}
        <section className="border border-zinc-800 bg-zinc-950/40 rounded-sm overflow-hidden" data-testid="otto-snippet-card">
          <div className="px-6 py-5 border-b border-zinc-800 flex items-start gap-4">
            <div className="w-11 h-11 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center flex-shrink-0">
              <Bot className="w-5 h-5 text-[#3377FF]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-lg font-semibold tracking-tight">Your install snippet</h2>
                {install?.alive && (
                  <Badge className="rounded-sm bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 text-[10px] tracking-widest uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] inline-block mr-1.5 animate-pulse" /> Live
                  </Badge>
                )}
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed mt-1.5">
                Paste this ONCE inside your site's <code className="text-[#3377FF]">&lt;head&gt;</code>. From now on, every audit fix is applied automatically — meta tags, schema, internal links, alt text.
              </p>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            {loading ? (
              <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : install?.snippet ? (
              <>
                <div className="bg-black border border-zinc-800 rounded-sm p-4 font-mono text-xs text-zinc-300 overflow-x-auto relative">
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[9px] uppercase">snippet</Badge>
                  </div>
                  <code data-testid="otto-snippet-code">{install.snippet}</code>
                </div>
                <Button onClick={copySnippet} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="otto-copy-snippet">
                  {copied ? <><Check className="w-4 h-4 mr-2" /> Copied — paste it inside &lt;head&gt;</> : <><Copy className="w-4 h-4 mr-2" /> Copy snippet</>}
                </Button>
              </>
            ) : (
              <Button onClick={generate} disabled={installing} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-12 px-6" data-testid="otto-generate">
                {installing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Bot className="w-4 h-4 mr-2" /> Generate my OTTO snippet</>}
              </Button>
            )}
          </div>
        </section>

        {/* What OTTO does */}
        <section className="grid sm:grid-cols-3 gap-3" data-testid="otto-features">
          <FeatureTile icon={Code2} title="Meta tags" desc="Title, description, OpenGraph, Twitter Card — pushed live without touching your theme." />
          <FeatureTile icon={Sparkles} title="Schema markup" desc="JSON-LD for Organization, Article, FAQ, Breadcrumb — auto-injected per page." />
          <FeatureTile icon={Zap} title="Patches" desc="Alt text, internal links, heading hierarchy fixes — applied via smart selectors." />
        </section>

        {/* How it works */}
        <section className="border border-zinc-800 bg-zinc-950/40 rounded-sm p-6" data-testid="otto-howto">
          <h3 className="font-display font-medium mb-4">How it works</h3>
          <ol className="space-y-2.5 text-sm text-zinc-400 leading-relaxed">
            <li className="flex gap-3"><span className="w-5 h-5 rounded-sm bg-[#0055FF]/15 text-[#3377FF] text-[10px] grid place-items-center font-mono flex-shrink-0">1</span> Generate your snippet above and paste it in your site's <code className="text-[#3377FF]">&lt;head&gt;</code>.</li>
            <li className="flex gap-3"><span className="w-5 h-5 rounded-sm bg-[#0055FF]/15 text-[#3377FF] text-[10px] grid place-items-center font-mono flex-shrink-0">2</span> The next time someone visits your site, OTTO fetches the latest fix kit and applies it instantly.</li>
            <li className="flex gap-3"><span className="w-5 h-5 rounded-sm bg-[#0055FF]/15 text-[#3377FF] text-[10px] grid place-items-center font-mono flex-shrink-0">3</span> Every new SEO audit auto-pushes new fixes — no redeploys, no manual work.</li>
            <li className="flex gap-3"><span className="w-5 h-5 rounded-sm bg-[#0055FF]/15 text-[#3377FF] text-[10px] grid place-items-center font-mono flex-shrink-0">4</span> Track everything from this page. The "Live" badge appears when OTTO has run on your site in the last 30 minutes.</li>
          </ol>
        </section>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ label, value, icon: Icon, color, small, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-eyebrow">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
      </div>
      <div className={`font-display font-semibold tracking-tight ${small ? "text-base" : "text-2xl"}`} style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function FeatureTile({ icon: Icon, title, desc }) {
  return (
    <div className="border border-zinc-800 rounded-sm p-4 bg-zinc-950">
      <div className="w-9 h-9 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center mb-3">
        <Icon className="w-4 h-4 text-[#3377FF]" />
      </div>
      <div className="font-medium text-sm">{title}</div>
      <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}
